/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NetworkSecurityPolicyModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import {
    IAviDataGridConfig,
    IRowReorder,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import { RepeatedMessageItem }
    from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import {
    networkSecurityPolicyActionTypeHash,
    NetworkSecurityRuleConfigItem,
} from 'ajs/modules/network-security-policy/factories/network-security-rule.config-item.factory';

import { L10nService } from '@vmw/ngx-vip';
import * as l10n from './network-security-policy-rules-grid.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *
 *  Network Security Policy Rules Grid Component used in Network Security Policy Modal.
 *
 * @author Abhinesh Gour
 */
@Component({
    selector: 'network-security-policy-rules-grid',
    templateUrl: './network-security-policy-rules-grid.component.html',
})
export class NetworkSecurityPolicyRulesGridComponent implements AfterViewInit {
    /**
     * NetworkSecurityRule RepeatedMessageItem.
     */
    @Input()
    public rules: RepeatedMessageItem<NetworkSecurityRuleConfigItem>;

    /**
     * Expanded content TemplateRef.
     */
    @ViewChild('expandedContentTemplateRef')
    public expandedContentTemplateRef: TemplateRef<HTMLElement>;

    /**
     * TemplateRef for displaying Enable icon.
     */
    @ViewChild('enabledColumnTemplateRef')
    public enabledColumnTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Fires on add Rule.
     */
    @Output()
    public onAddRule = new EventEmitter<void>();

    /**
     * Fires on edit Rule.
     */
    @Output()
    public onEditRule = new EventEmitter<NetworkSecurityRuleConfigItem>();

    /**
     * Fires on delete Rule.
     */
    @Output()
    public onDeleteRule = new EventEmitter<NetworkSecurityRuleConfigItem>();

    /**
     * Fires on re-ordering of a Rule.
     */
    @Output()
    public onRowOrderChange = new EventEmitter<IRowReorder>();

    /**
     * Network Security policy rules grid config.
     */
    public rulesGridConfig: IAviDataGridConfig;

    /**
     * Holds values of networkSecurityPolicyActionType enum to use in template.
     */
    public readonly networkSecurityPolicyActionType = networkSecurityPolicyActionTypeHash;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Needed because expandedContentTemplateRef for avi-data-grid
     * can only be set afterViewInit.
     * @override
     */
    public ngAfterViewInit(): void {
        this.setRuleGridProps();
    }

    /**
     * Emits an event on click of add button from template.
     */
    public addRule(): void {
        this.onAddRule.emit();
    }

    /**
     * Emits an event on click of edit button from template.
     */
    public editRule(rule: NetworkSecurityRuleConfigItem): void {
        this.onEditRule.emit(rule);
    }

    /**
     * Emits rule reorder event.
     */
    public handleRowReorder(reorderInfo: IRowReorder): void {
        this.onRowOrderChange.emit(reorderInfo);
    }

    /**
     * Emits an event on click of delete button from template.
     */
    private deleteRule(rule: NetworkSecurityRuleConfigItem): void {
        this.onDeleteRule.emit(rule);
    }

    /**
     * Sets config and rows for NetworkSecurityRule grid.
     */
    private setRuleGridProps(): void {
        const { l10nService } = this;

        this.rulesGridConfig = {
            layout: {
                withReordering: true,
            },
            expandedContentTemplateRef: this.expandedContentTemplateRef,
            fields: [
                {
                    label: l10nService.getMessage(l10nKeys.nameLabel),
                    id: 'name',
                    transform: (row: NetworkSecurityRuleConfigItem) => {
                        return row.config.name;
                    },
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.enableLabel),
                    id: 'enable',
                    templateRef: this.enabledColumnTemplateRef,
                },
            ],
            multipleactions: [{
                label: l10nService.getMessage(l10nKeys.removeButtonLabel),
                onClick: (ruleList: NetworkSecurityRuleConfigItem[]) => {
                    ruleList.forEach((rule: NetworkSecurityRuleConfigItem) => {
                        this.deleteRule(rule);
                    });
                },
            }],
            singleactions: [
                {
                    label: this.l10nService.getMessage(l10nKeys.editButtonLabel),
                    shape: 'pencil',
                    onClick: (rule: NetworkSecurityRuleConfigItem) => {
                        this.editRule(rule);
                    },
                },
                {
                    label: l10nService.getMessage(l10nKeys.removeButtonLabel),
                    shape: 'trash',
                    onClick: (rule: NetworkSecurityRuleConfigItem) => {
                        return this.deleteRule(rule);
                    },
                },
            ],
        };
    }
}
